import * as THREE from 'three';
import {createReferencePlaneHostFaceDescriptor, type ReferencePlaneHostFaceDescriptor,} from './ReferencePlaneHostFace';

let count = 0

/**
 * 参考面显式轴向输入。
 */
export interface ReferencePlaneAxisInput {
    /** 平面 X 轴。 */
    xAxis?: THREE.Vector3;
    /** 平面 Y 轴。 */
    yAxis?: THREE.Vector3;
}

/**
 * 抽象基准面：统一承载草图平面的法线/常量定义。
 * 说明：
 * 1. 提供坐标系、局部-世界坐标转换等数学能力；
 * 2. 不包含任何可视化逻辑，可视化由子类（如 DatumPlane）负责。
 */
export class ReferencePlane extends THREE.Plane {
    /** 基准面唯一 ID。 */
    readonly id: number = 0;
    /** 显示名称。 */
    public displayName: string;
    /** 平面 X 轴存储。 */
    private xAxisVector: THREE.Vector3;
    /** 平面 Y 轴存储。 */
    private yAxisVector: THREE.Vector3;
    /** 宿主面的稳定描述。 */
    private readonly hostFaceDescriptor: ReferencePlaneHostFaceDescriptor | null;
    /** 原点计算复用缓存。 */
    private readonly originWorldBuffer: THREE.Vector3 = new THREE.Vector3();
    /** 世界转局部的相对向量缓存。 */
    private readonly relativeWorldBuffer: THREE.Vector3 = new THREE.Vector3();
    /** 世界点投影到当前平面时复用的局部二维坐标。 */
    private readonly projectedLocalPointBuffer: THREE.Vector2 = new THREE.Vector2();

    constructor(
        displayName: string,
        normal: THREE.Vector3,
        constant: number,
        axisInput: ReferencePlaneAxisInput = {},
        hostFace: ReferencePlaneHostFaceDescriptor | null = null,
        idOverride?: number,
    ) {
        super(normal, constant);

        // Undo/Redo 恢复时必须保留基准面数值 ID，否则依赖 datum-plane:N 的后续引用会断开。
        if (Number.isFinite(idOverride)) {
            this.id = Number(idOverride);
            count = Math.max(count, this.id + 1);
        } else {
            this.id = count
            count += 1;
        }

        this.displayName = displayName;
        const {xAxis, yAxis} = this.resolvePlanarAxes(normal, axisInput);
        this.xAxisVector = xAxis;
        this.yAxisVector = yAxis;
        this.hostFaceDescriptor = hostFace
            ? createReferencePlaneHostFaceDescriptor(hostFace)
            : null;
    }

    /**
     * 获取当前基准面原点（世界坐标）。
     */
    public get originWorld(): THREE.Vector3 {
        return this.coplanarPoint(new THREE.Vector3());
    }

    /**
     * 获取当前基准面 X 轴（单位向量）。
     */
    public get xAxis(): THREE.Vector3 {
        return this.xAxisVector.clone();
    }

    /**
     * 获取当前基准面 Y 轴（单位向量）。
     */
    public get yAxis(): THREE.Vector3 {
        return this.yAxisVector.clone();
    }


    /**
     * 更新参考面的世界坐标系。
     *
     * 设计说明：
     * 1. 装配体零部件移动后，由特征面计算出的基准面必须跟随新位置重算；
     * 2. 只更新 `THREE.Plane.normal/constant` 不够，草图局部坐标还依赖 X/Y 轴；
     * 3. 因此这里统一更新平面数学定义和局部坐标轴。
     *
     * @param originWorld 平面原点（世界坐标）。
     * @param normal 平面法向。
     * @param axisInput 平面轴向输入。
     */
    public updateReferenceFrame(
        originWorld: THREE.Vector3,
        normal: THREE.Vector3,
        axisInput: ReferencePlaneAxisInput = {},
    ): void {
        const normalizedNormal = normal.clone().normalize();
        const {xAxis, yAxis} = this.resolvePlanarAxes(normalizedNormal, axisInput);
        this.setFromNormalAndCoplanarPoint(normalizedNormal, originWorld);
        this.xAxisVector = xAxis;
        this.yAxisVector = yAxis;
    }


    /**
     * 获取参考平面的宿主面描述。
     *
     * 设计说明：
     * 1. 仅面驱动草图平面会携带宿主面；
     * 2. 默认基准面与普通构造平面返回 null；
     * 3. 返回冻结后的稳定对象，供选择链路安全复用。
     */
    public get hostFace(): ReferencePlaneHostFaceDescriptor | null {
        return this.hostFaceDescriptor;
    }

    /**
     * 将基准面局部 2D 点映射到世界 3D 点。
     * @param localPoint 局部点。
     */
    public localToWorld(localPoint: THREE.Vector2): THREE.Vector3;
    /**
     * 将基准面局部 2D 点映射到世界 3D 点。
     * @param x 局部坐标 X 分量。
     * @param y 局部坐标 Y 分量。
     */
    public localToWorld(x: number, y: number): THREE.Vector3;
    public localToWorld(xOrPoint: THREE.Vector2 | number, y?: number): THREE.Vector3 {
        if (typeof xOrPoint === 'number') {
            return this.localToWorldTo(xOrPoint, y!, new THREE.Vector3());
        }
        return this.localToWorldTo(xOrPoint, new THREE.Vector3());
    }

    /**
     * 将基准面局部 2D 点写入指定世界坐标向量。
     * @param localPoint 局部点。
     * @param out 输出世界坐标。
     */
    public localToWorldTo(localPoint: THREE.Vector2, out: THREE.Vector3): THREE.Vector3;
    /**
     * 将基准面局部 2D 点写入指定世界坐标向量。
     * @param x 局部坐标 X 分量。
     * @param y 局部坐标 Y 分量。
     * @param out 输出世界坐标。
     */
    public localToWorldTo(x: number, y: number, out: THREE.Vector3): THREE.Vector3;
    public localToWorldTo(
        xOrPoint: THREE.Vector2 | number,
        yOrOut: number | THREE.Vector3,
        outMaybe?: THREE.Vector3,
    ): THREE.Vector3 {
        const lx = typeof xOrPoint === 'number' ? xOrPoint : xOrPoint.x;
        const ly = typeof xOrPoint === 'number' ? yOrOut as number : xOrPoint.y;
        const out = typeof xOrPoint === 'number' ? outMaybe! : yOrOut as THREE.Vector3;
        return this.copyOriginWorldTo(out)
            .addScaledVector(this.xAxisVector, lx)
            .addScaledVector(this.yAxisVector, ly);
    }

    /**
     * 将世界 3D 点投影到基准面局部 2D 坐标。
     * @param worldPoint 世界坐标点。
     */
    public worldToLocal(worldPoint: THREE.Vector3): THREE.Vector2;
    /**
     * 将世界 3D 点投影到基准面局部 2D 坐标。
     * @param x 世界坐标 X 分量。
     * @param y 世界坐标 Y 分量。
     * @param z 世界坐标 Z 分量。
     */
    public worldToLocal(x: number, y: number, z: number): THREE.Vector2;
    public worldToLocal(xOrPoint: THREE.Vector3 | number, y?: number, z?: number): THREE.Vector2 {
        if (typeof xOrPoint === 'number') {
            return this.worldToLocalTo(xOrPoint, y!, z!, new THREE.Vector2());
        }
        return this.worldToLocalTo(xOrPoint, new THREE.Vector2());
    }

    /**
     * 将世界 3D 点投影到指定局部 2D 向量。
     * @param worldPoint 世界坐标点。
     * @param out 输出局部坐标。
     */
    public worldToLocalTo(worldPoint: THREE.Vector3, out: THREE.Vector2): THREE.Vector2;
    /**
     * 将世界 3D 点投影到指定局部 2D 向量。
     * @param x 世界坐标 X 分量。
     * @param y 世界坐标 Y 分量。
     * @param z 世界坐标 Z 分量。
     * @param out 输出局部坐标。
     */
    public worldToLocalTo(x: number, y: number, z: number, out: THREE.Vector2): THREE.Vector2;
    public worldToLocalTo(
        xOrPoint: THREE.Vector3 | number,
        yOrOut: number | THREE.Vector2,
        zOrOut?: number | THREE.Vector2,
        outMaybe?: THREE.Vector2,
    ): THREE.Vector2 {
        const worldX = typeof xOrPoint === 'number' ? xOrPoint : xOrPoint.x;
        const worldY = typeof xOrPoint === 'number' ? yOrOut as number : xOrPoint.y;
        const worldZ = typeof xOrPoint === 'number' ? zOrOut as number : xOrPoint.z;
        const out = typeof xOrPoint === 'number' ? outMaybe! : yOrOut as THREE.Vector2;
        const relative = this.relativeWorldBuffer
            .set(worldX, worldY, worldZ)
            .sub(this.copyOriginWorldTo(this.originWorldBuffer));
        return out.set(relative.dot(this.xAxisVector), relative.dot(this.yAxisVector));
    }

    /**
     * 将世界点正交投影到当前参考面。
     *
     * 设计说明：
     * 1. 草图参数曲线完全位于参考面内，输入点沿法向的距离不会改变最近曲线参数；
     * 2. 若直接把法向距离并入距离平方，高精度参数细化会因大常量项产生浮点消减；
     * 3. 因此统一通过世界转局部、局部转世界完成平面投影，确保倾斜草图也使用同一语义。
     *
     * @param worldPoint 待投影世界坐标。
     * @returns 当前参考面上的独立世界坐标。
     */
    public projectWorldPointToPlane(worldPoint: THREE.Vector3): THREE.Vector3 {
        return this.projectWorldPointToPlaneTo(worldPoint, new THREE.Vector3());
    }

    /**
     * 将世界点正交投影到当前参考面并写入指定向量。
     *
     * @param worldPoint 待投影世界坐标。
     * @param out 输出世界坐标。
     * @returns 写入后的输出向量。
     */
    public projectWorldPointToPlaneTo(worldPoint: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 {
        this.worldToLocalTo(worldPoint, this.projectedLocalPointBuffer);
        return this.localToWorldTo(this.projectedLocalPointBuffer, out);
    }

    /**
     * 将当前参考面的原点写入目标向量。
     *
     * @param out 输出世界坐标。
     * @returns 当前参考面原点。
     */
    public copyOriginWorldTo(out: THREE.Vector3): THREE.Vector3 {
        return this.coplanarPoint(out);
    }

    /**
     * 基于法线构建平面 Y 轴（向上方向）。
     * 规则：
     * 1. 优先使用世界上方向 (0,1,0) 投影到平面；
     * 2. 若退化则回退到世界前方向 (0,0,1) 投影。
     */
    private resolvePlanarYAxis(normal: THREE.Vector3): THREE.Vector3 {
        const worldUp = new THREE.Vector3(0, 1, 0);
        const projectedUp = worldUp.clone().sub(normal.clone().multiplyScalar(worldUp.dot(normal)));
        if (projectedUp.lengthSq() > 1e-12) {
            return projectedUp.normalize();
        }

        const worldForward = new THREE.Vector3(0, 0, 1);
        const projectedForward = worldForward.clone().sub(normal.clone().multiplyScalar(worldForward.dot(normal)));
        if (projectedForward.lengthSq() > 1e-12) {
            return projectedForward.normalize();
        }

        throw new Error('参考面法线无效，无法求解平面 Y 轴');
    }

    /**
     * 解析显式轴向或默认轴向。
     *
     * @param normal 平面法向。
     * @param axisInput 显式轴向输入。
     * @returns 正交化后的平面轴向。
     */
    private resolvePlanarAxes(normal: THREE.Vector3, axisInput: ReferencePlaneAxisInput): {
        xAxis: THREE.Vector3;
        yAxis: THREE.Vector3;
    } {
        if (!axisInput.xAxis && !axisInput.yAxis) {
            const yAxis = this.resolvePlanarYAxis(normal);
            return {
                xAxis: yAxis.clone().cross(normal).normalize(),
                yAxis,
            };
        }

        const projectedXAxis = axisInput.xAxis
            ? this.projectVectorOnPlane(axisInput.xAxis, normal)
            : null;
        const projectedYAxis = axisInput.yAxis
            ? this.projectVectorOnPlane(axisInput.yAxis, normal)
            : null;

        if (!projectedXAxis && !projectedYAxis) {
            throw new Error('参考面显式轴向无效，无法求解平面坐标系');
        }

        let xAxis = projectedXAxis ?? projectedYAxis!.clone().cross(normal).normalize();
        let yAxis = normal.clone().cross(xAxis).normalize();

        if (projectedYAxis && yAxis.dot(projectedYAxis) < 0) {
            xAxis = xAxis.clone().negate();
            yAxis = yAxis.clone().negate();
        }

        return {
            xAxis,
            yAxis,
        };
    }

    /**
     * 将向量投影到当前平面内。
     *
     * @param vector 输入向量。
     * @param normal 平面法向。
     * @returns 投影向量；退化时返回 null。
     */
    private projectVectorOnPlane(vector: THREE.Vector3, normal: THREE.Vector3): THREE.Vector3 | null {
        const projected = vector
            .clone()
            .sub(normal.clone().multiplyScalar(vector.dot(normal)));
        if (projected.lengthSq() <= 1e-12) {
            return null;
        }
        return projected.normalize();
    }
}
